import { ArrowLeft, Calendar, Clock, User, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import BlogPostImage from "@/components/BlogPostImage";

const BlogPost1 = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Article */}
      <article className="py-12 md:py-20 px-4 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <Button asChild variant="ghost" className="mb-8 gap-2 hover:gap-3 transition-all">
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4" /> Voltar ao Blog
            </Link>
          </Button>

          <Card className="border-border/50 bg-card/50 backdrop-blur-sm p-8 md:p-12">
            {/* Featured Image */}
            <div className="aspect-video w-full rounded-lg mb-8 overflow-hidden">
              <BlogPostImage
                title="Tendências em Desenvolvimento Web para 2026"
                backgroundImage="/assets/blog-tendencias-web-2026.webp"
                icon={<Code2 className="w-full h-full" />}
              />
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>15 de Novembro, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>8 min de leitura</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Equipe Coddar</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Tendências em Desenvolvimento Web para 2026
            </h1>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-muted-foreground mb-6">
                O desenvolvimento web está em constante evolução, e 2026 promete trazer inovações significativas que vão transformar a forma como criamos e interagimos com aplicações web. Neste artigo, exploramos as principais tendências que estão moldando o futuro da tecnologia.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">1. Inteligência Artificial Integrada</h2>
              <p className="text-muted-foreground mb-4">
                A IA não é mais apenas uma funcionalidade adicional - está se tornando parte fundamental da arquitetura web. Frameworks modernos como Next.js e Remix estão integrando recursos de IA nativamente, permitindo:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Personalização de conteúdo em tempo real</li>
                <li>Chatbots e assistentes virtuais mais inteligentes</li>
                <li>Otimização automática de performance</li>
                <li>Geração de código e componentes assistida por IA</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">2. WebAssembly (WASM) em Ascensão</h2>
              <p className="text-muted-foreground mb-4">
                O WebAssembly está revolucionando o que é possível fazer no navegador. Com performance próxima ao código nativo, WASM permite:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Aplicações web com performance de desktop</li>
                <li>Jogos 3D complexos rodando no navegador</li>
                <li>Ferramentas de edição de vídeo e imagem online</li>
                <li>Portabilidade de código entre diferentes plataformas</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">3. Edge Computing e Serverless</h2>
              <p className="text-muted-foreground mb-4">
                A computação na borda (edge) está mudando a arquitetura de aplicações web. Plataformas como Cloudflare Workers, Vercel Edge Functions e Deno Deploy permitem:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Latência ultra-baixa para usuários globais</li>
                <li>Processamento mais próximo do usuário final</li>
                <li>Redução de custos com infraestrutura</li>
                <li>Escalabilidade automática e instantânea</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">4. Frameworks Meta e Compiladores Inteligentes</h2>
              <p className="text-muted-foreground mb-4">
                Novos frameworks estão focando em otimização em tempo de compilação. Ferramentas como Astro, Qwik e Solid.js trazem:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Zero JavaScript por padrão (ship less JS)</li>
                <li>Hidratação parcial e sob demanda</li>
                <li>Renderização no servidor mais eficiente</li>
                <li>Melhor performance em dispositivos móveis</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">5. Web3 e Descentralização</h2>
              <p className="text-muted-foreground mb-4">
                Embora ainda em evolução, tecnologias Web3 estão ganhando tração prática:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Autenticação descentralizada (DID)</li>
                <li>Armazenamento distribuído (IPFS)</li>
                <li>Smart contracts para lógica de negócio</li>
                <li>NFTs e ativos digitais integrados</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">6. Progressive Web Apps (PWA) 2.0</h2>
              <p className="text-muted-foreground mb-4">
                PWAs estão evoluindo com novas APIs e capacidades:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Acesso a hardware nativo (Bluetooth, NFC, USB)</li>
                <li>Sincronização em background mais robusta</li>
                <li>Instalação mais simples e intuitiva</li>
                <li>Integração profunda com o sistema operacional</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Conclusão</h2>
              <p className="text-muted-foreground mb-4">
                O futuro do desenvolvimento web é empolgante e cheio de possibilidades. Essas tendências não são apenas hype - elas representam mudanças fundamentais na forma como construímos aplicações web. Na Coddar, estamos sempre atualizados com as últimas tecnologias para entregar soluções modernas e eficientes aos nossos clientes.
              </p>
              <p className="text-muted-foreground">
                Quer implementar essas tecnologias no seu projeto? Entre em contato conosco e descubra como podemos ajudar sua empresa a estar na vanguarda da inovação tecnológica.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-12 p-6 bg-primary/10 border border-primary/20 rounded-lg">
              <h3 className="text-xl font-bold text-foreground mb-2">Precisa de um projeto web moderno?</h3>
              <p className="text-muted-foreground mb-4">
                Nossa equipe está pronta para transformar suas ideias em realidade usando as tecnologias mais avançadas do mercado.
              </p>
              <Button asChild>
                <Link to="/contato">Fale Conosco</Link>
              </Button>
            </div>
          </Card>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost1;
